import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Observable, delay, merge } from 'rxjs';
import { AppState } from '../../../app.reducer';
import { ButtonComponent } from '../../button/button.component';
import { UserAccount } from '../../../interfaces/user-account';
import { logout } from '../../../store/auth/auth.actions';
import { loadUserAccount } from '../../../store/user-account/user-account.actions';
import { selectUserAccount } from '../../../store/user-account/user-account.reducer';

@UntilDestroy()
@Component({
  selector: 'app-tfa-block-modal',
  templateUrl: './tfa-block-modal.component.html',
  styleUrls: ['./tfa-block-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent, TranslocoPipe, ButtonComponent],
})
export class TfaBlockModalComponent implements OnInit, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store<AppState>);

  @ViewChild('tfaRequiredModal') tfaRequiredModal: ModalComponent;
  @ViewChild('tfaRequiredBlockedModal') tfaRequiredBlockedModal: ModalComponent;
  @Output() triggerAddMethodModal: EventEmitter<any> = new EventEmitter<any>();
  private tfaRequiredSince: number;

  userAccount$!: Observable<UserAccount>;

  ngOnInit(): void {
    this.store.dispatch(loadUserAccount());
    this.userAccount$ = this.store.select(selectUserAccount);

    this.userAccount$.pipe(delay(2000), untilDestroyed(this)).subscribe((user: UserAccount) => {
      if (!user?.hasTFA && user?.tfaRequiredSince) {
        // check if user needs to have tfa
        this.tfaRequiredSince = user.tfaRequiredSince;
        if (this.tfaRequiredSinceDays >= 7) {
          // check if user is past tfa deadline
          this.tfaRequiredBlockedModal.open();
        } else {
          this.tfaRequiredModal.open();
        }
      }
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    merge(
      this.tfaRequiredModal.onOpen,
      this.tfaRequiredModal.onClose,
      this.tfaRequiredBlockedModal.onOpen,
      this.tfaRequiredBlockedModal.onClose
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => this.cdr.markForCheck());
  }

  logout() {
    this.store.dispatch(logout());
  }

  get tfaRequiredRemainingDays(): number {
    return this.tfaRequiredSince
      ? 7 - Math.round(-1 * DateTime.fromSeconds(this.tfaRequiredSince).diffNow('days').days)
      : null;
  }

  get tfaRequiredSinceDays(): number {
    return this.tfaRequiredSince
      ? Math.round(-1 * DateTime.fromSeconds(this.tfaRequiredSince).diffNow('days').days)
      : null;
  }
}
